/**
 * Product Link Prefetching
 * 
 * Provides world-class prefetching for product links (siblings, upsells, etc.) with:
 * - Speculation Rules API for modern browsers (Chrome 121+, Edge 121+)
 * - Intersection Observer + touchstart + hover fallback for older browsers
 * - Image preloading for instant visual feedback
 * - Performance safeguards (data saver, slow connections, deduplication)
 */

(function() {
  'use strict';

  // Gemini2: Prevent duplicate execution when script is loaded by multiple sections
  if (window._siblingPrefetchLoaded) return;
  window._siblingPrefetchLoaded = true;

  // Configuration
  const CONFIG = {
    maxPrefetchOnViewport: 3,      // Max links to prefetch when entering viewport
    hoverDelay: 150,               // Delay before prefetching on hover (ms)
    // ANATE-PERF R15: was 0.5, but a collection grid taller than the viewport can never
    // reach 50% visibility, so its observer never fired. Any intersection now triggers.
    intersectionThreshold: 0,
    
    // Prefetch zones - each zone defines a container and its links
    zones: {
      siblings: {
        container: '[data-sibling-prefetch-container]',
        link: '.sibling__link:not(.sibling__link--current)',
        imageAttr: 'data-sibling-image'
      },
      upsell: {
        container: '[data-upsell-prefetch-container]',
        link: '.gemini2-upsell-card__link',
        imageAttr: 'data-upsell-image'
      },
      // ANATE-PERF R15: collection grid now gets viewport prefetch too (capped at
      // maxPrefetchOnViewport links, i.e. the first few cards) so the most likely
      // next navigations are already cached when the visitor clicks.
      collectionGrid: {
        container: '[data-collection-grid-prefetch]',
        link: '.product-grid-item a[href*="/products/"], .product-link[href*="/products/"]',
        imageAttr: 'data-product-image'
      },
      // Related products (AJAX-loaded)
      relatedProducts: {
        container: '[data-related-section]',
        link: '.product-grid-item a[href*="/products/"], .product-link[href*="/products/"]',
        imageAttr: 'data-product-image',
        isAjax: true
      },
      // Recently viewed products (AJAX-loaded)
      recentlyViewed: {
        container: 'recently-viewed',
        link: '.product-grid-item a[href*="/products/"], .product-link[href*="/products/"]',
        imageAttr: 'data-product-image',
        isAjax: true
      }
    }
  };

  // Track prefetched URLs to avoid duplicates
  const prefetchedUrls = new Set();
  const preloadedImages = new Set();

  /**
   * Check if Speculation Rules API is supported
   */
  function supportsSpeculationRules() {
    return HTMLScriptElement.supports && HTMLScriptElement.supports('speculationrules');
  }

  /**
   * ANATE-PERF R15: inject a document-level Speculation Rules prefetch rule.
   * Previously the code assumed the browser "handles it natively" when the API is
   * supported, but no rules were ever injected -- so Chrome/Edge got NO document
   * prefetch at all. This rule makes the browser prefetch any product link on
   * hover/touch ("moderate" eagerness), including AJAX-injected ones (document
   * source rules match links added later), with built-in dedupe and cache limits.
   */
  function injectSpeculationRules() {
    if (!supportsSpeculationRules() || !shouldPrefetch()) {
      return;
    }
    if (document.querySelector('script[data-anate-speculation-rules]')) {
      return;
    }

    const script = document.createElement('script');
    script.type = 'speculationrules';
    script.setAttribute('data-anate-speculation-rules', 'true');
    script.textContent = JSON.stringify({
      prefetch: [
        {
          source: 'document',
          where: {
            href_matches: ['/products/*', '/collections/*/products/*']
          },
          eagerness: 'moderate'
        }
      ]
    });
    document.head.appendChild(script);
  }

  /**
   * Check if prefetching should be allowed based on network conditions
   */
  function shouldPrefetch() {
    // Respect Data Saver mode
    if (navigator.connection?.saveData) {
      return false;
    }

    // Skip on very slow connections
    const effectiveType = navigator.connection?.effectiveType;
    if (effectiveType === '2g' || effectiveType === 'slow-2g') {
      return false;
    }

    // Respect reduced motion preference (optional - mainly for animations)
    // For prefetching, we still allow it as it doesn't cause motion
    
    return true;
  }

  /**
   * Prefetch a URL by injecting a link[rel=prefetch] element
   */
  function prefetchLink(url) {
    if (!url || prefetchedUrls.has(url) || !shouldPrefetch()) {
      return;
    }

    // Normalize URL
    const normalizedUrl = new URL(url, window.location.origin).href;
    
    if (prefetchedUrls.has(normalizedUrl)) {
      return;
    }

    // Check if already prefetched by browser (speculation rules)
    const existingPrefetch = document.querySelector(`link[rel="prefetch"][href="${normalizedUrl}"]`);
    if (existingPrefetch) {
      prefetchedUrls.add(normalizedUrl);
      return;
    }

    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = normalizedUrl;
    link.as = 'document';
    
    document.head.appendChild(link);
    prefetchedUrls.add(normalizedUrl);
  }

  /**
   * Preload an image by injecting a link[rel=preload] element
   */
  function preloadImage(src) {
    if (!src || preloadedImages.has(src) || !shouldPrefetch()) {
      return;
    }

    // Check if already preloaded
    const existingPreload = document.querySelector(`link[rel="preload"][href="${src}"]`);
    if (existingPreload) {
      preloadedImages.add(src);
      return;
    }

    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = src;
    
    document.head.appendChild(link);
    preloadedImages.add(src);
  }

  /**
   * PrefetchZone class - handles all prefetching logic for a container
   * Works with any zone configuration (siblings, upsells, etc.)
   */
  class PrefetchZone {
    constructor(container, zoneConfig) {
      this.container = container;
      this.zoneConfig = zoneConfig;
      this.links = container.querySelectorAll(zoneConfig.link);
      this.imageAttr = zoneConfig.imageAttr;
      this.hoverTimers = new Map();
      this.initialized = false;

      // When Speculation Rules is supported, hover/touch document prefetch is handled
      // by the injected rule (see injectSpeculationRules). We still add image
      // preloading, and (ANATE-PERF R15) viewport prefetch of the first few links,
      // which the hover-based speculation rule doesn't cover.
      if (supportsSpeculationRules()) {
        this.initImagePreloading();
        if (!this.zoneConfig.skipViewportPrefetch) {
          this.initIntersectionObserver();
        }
        return;
      }

      this.init();
    }

    init() {
      if (this.initialized || this.links.length === 0) {
        return;
      }

      // Skip viewport prefetch for large grids (collection pages)
      if (!this.zoneConfig.skipViewportPrefetch) {
        this.initIntersectionObserver();
      }
      this.initTouchHandlers();
      this.initHoverHandlers();
      // Note: Image preloading is handled within touch/hover handlers above
      // No need to call initImagePreloading() separately here
      
      this.initialized = true;
    }

    /**
     * Intersection Observer - prefetch siblings when they enter viewport
     */
    initIntersectionObserver() {
      if (!('IntersectionObserver' in window)) {
        return;
      }

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            // Prefetch first N siblings when container becomes visible
            const links = Array.from(this.links).slice(0, CONFIG.maxPrefetchOnViewport);
            links.forEach(link => {
              prefetchLink(link.href);
            });
            
            // Only observe once
            observer.unobserve(entry.target);
          }
        });
      }, {
        threshold: CONFIG.intersectionThreshold,
        rootMargin: '50px' // Start prefetching slightly before entering viewport
      });

      observer.observe(this.container);
    }

    /**
     * Touch handlers - prefetch on touchstart (fires before click)
     */
    initTouchHandlers() {
      this.links.forEach(link => {
        link.addEventListener('touchstart', () => {
          prefetchLink(link.href);
          this.preloadLinkImage(link);
        }, { passive: true });
      });
    }

    /**
     * Hover handlers - prefetch on mouseenter with delay
     */
    initHoverHandlers() {
      this.links.forEach(link => {
        link.addEventListener('mouseenter', () => {
          // Clear any existing timer
          if (this.hoverTimers.has(link)) {
            clearTimeout(this.hoverTimers.get(link));
          }

          // Set new timer
          const timer = setTimeout(() => {
            prefetchLink(link.href);
            this.preloadLinkImage(link);
            this.hoverTimers.delete(link);
          }, CONFIG.hoverDelay);

          this.hoverTimers.set(link, timer);
        });

        link.addEventListener('mouseleave', () => {
          if (this.hoverTimers.has(link)) {
            clearTimeout(this.hoverTimers.get(link));
            this.hoverTimers.delete(link);
          }
        });

        // Also handle focus for keyboard navigation
        link.addEventListener('focusin', () => {
          prefetchLink(link.href);
          this.preloadLinkImage(link);
        });
      });
    }

    /**
     * Image preloading - for faster visual feedback
     * This runs even when Speculation Rules is supported
     */
    initImagePreloading() {
      this.links.forEach(link => {
        // Touch
        link.addEventListener('touchstart', () => {
          this.preloadLinkImage(link);
        }, { passive: true });

        // Hover
        link.addEventListener('mouseenter', () => {
          this.preloadLinkImage(link);
        });

        // Focus
        link.addEventListener('focusin', () => {
          this.preloadLinkImage(link);
        });
      });
    }

    /**
     * Preload the featured image for a product link
     */
    preloadLinkImage(link) {
      const imageSrc = link.getAttribute(this.imageAttr);
      if (imageSrc) {
        preloadImage(imageSrc);
      }
    }
  }

  /**
   * Initialize prefetching for all zones (siblings, upsells, etc.)
   */
  function initAllContainers() {
    // Iterate over all configured zones
    Object.keys(CONFIG.zones).forEach(zoneName => {
      const zoneConfig = CONFIG.zones[zoneName];
      const containers = document.querySelectorAll(zoneConfig.container);
      
      containers.forEach(container => {
        // Check if already initialized for this zone
        const initAttr = `data-prefetch-initialized-${zoneName}`;
        if (container.hasAttribute(initAttr)) {
          return;
        }
        
        new PrefetchZone(container, zoneConfig);
        container.setAttribute(initAttr, 'true');
      });
    });
  }

  /**
   * Initialize on DOM ready
   */
  injectSpeculationRules();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllContainers);
  } else {
    initAllContainers();
  }

  /**
   * Re-initialize when Shopify sections are loaded/reloaded (Theme Editor support)
   */
  document.addEventListener('shopify:section:load', initAllContainers);

  /**
   * MutationObserver for AJAX-loaded content (Related Products, Recently Viewed)
   * Watches containers marked with isAjax: true and re-initializes when content is added
   */
  function initAjaxObservers() {
    if (!('MutationObserver' in window)) {
      return;
    }

    const ajaxZones = Object.entries(CONFIG.zones).filter(([_, config]) => config.isAjax);
    
    ajaxZones.forEach(([zoneName, zoneConfig]) => {
      const containers = document.querySelectorAll(zoneConfig.container);
      
      containers.forEach(container => {
        // Check if already observing
        if (container.hasAttribute('data-prefetch-observing')) {
          return;
        }
        
        const observer = new MutationObserver((mutations) => {
          // Check if product links were added
          const hasNewLinks = mutations.some(mutation => 
            mutation.addedNodes.length > 0 && 
            Array.from(mutation.addedNodes).some(node => 
              node.nodeType === 1 && (
                node.matches?.(zoneConfig.link) || 
                node.querySelector?.(zoneConfig.link)
              )
            )
          );
          
          if (hasNewLinks) {
            // Small delay to ensure DOM is fully updated
            setTimeout(() => {
              // Re-query links and initialize
              const initAttr = `data-prefetch-initialized-${zoneName}`;
              container.removeAttribute(initAttr); // Allow re-initialization
              new PrefetchZone(container, zoneConfig);
              container.setAttribute(initAttr, 'true');
            }, 100);
          }
        });
        
        observer.observe(container, {
          childList: true,
          subtree: true
        });
        
        container.setAttribute('data-prefetch-observing', 'true');
      });
    });
  }

  // Initialize AJAX observers on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAjaxObservers);
  } else {
    initAjaxObservers();
  }

  /**
   * Expose for debugging/testing in development
   */
  if (window.Shopify?.designMode) {
    window.ProductPrefetch = {
      supportsSpeculationRules,
      shouldPrefetch,
      prefetchLink,
      preloadImage,
      prefetchedUrls,
      preloadedImages,
      zones: CONFIG.zones
    };
  }
})();

